<script setup>
import InputError from "@/Components/InputError.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import InputField from "@/Components/InputField.vue";
import InputLabel from "@/Components/InputLabel.vue";

import { defineProps, ref, watch } from "vue";
import Swal from "sweetalert2";
import { router, useForm, usePage } from "@inertiajs/vue3";
import axios from "axios";

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  jenis: {
    type: Object,
    required: true,
  },
  program: {
    type: Object,
    required: true,
  },
  standars: {
    type: Object,
    required: true,
  },
  parentFolderId: {
    type: [Number, String, null],
    default: null,
  },
  parentFolderName: {
    type: [String, null],
    default: null,
  },
});

// Mode:
// - 'file'    : upload dokumen biasa (multi)
// - 'folder'  : buat folder + opsional satu file
// - 'folderTree': unggah 1 folder berisi banyak file + subfolder (webkitdirectory)
const mode = ref('file');

const form = useForm({
  id: null,
  stdid: "",
  parent_id: null,
  // file mode fields
  nama_dok: "",
  jenis_dok: "",
  prodi: "",
  status: "",
  deskripsi: "",
  files: [],
});

// Folder mode specific refs
const folderName = ref("");
const folderFile = ref(null); // single file
const folderFileNama = ref("");
const folderFileDeskripsi = ref("");
// Tags (comma separated) for both modes
const rawTags = ref("");

// Folder tree upload refs
const folderTreeFiles = ref([]); // FileList as array
function handleFolderTreeChange(e) {
  const list = e.target.files ? Array.from(e.target.files) : [];
  folderTreeFiles.value = list;
}

watch(
  () => props.standars,
  (newValue) => {
    if (newValue && newValue.data && newValue.data.length > 0) {
      const firstItem = newValue.data[0];
      form.stdid = firstItem.id;
      form.jenis_dok = firstItem.jenis_id;
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => props.parentFolderId,
  (val) => {
    form.parent_id = val || null;
  },
  { immediate: true }
);

watch(
  () => props.program,
  (newValue) => {
    if (newValue) {
      form.prodi = newValue.id;
    }
  },
  { immediate: true }
);

function handleFileChange(e) {
  if (mode.value === 'file') {
    form.files = Array.from(e.target.files);
  } else {
    folderFile.value = e.target.files[0] || null;
  }
}
const fileInput = ref(null);

const submit = async () => {
  if (mode.value === 'file') {
    const formData = new FormData();
    formData.append("stdid", form.stdid);
    if (form.parent_id) formData.append("parent_id", form.parent_id);
    formData.append("nama_dok", form.nama_dok);
    formData.append("jenis_dok", form.jenis_dok);
    formData.append("prodi", form.prodi);
    formData.append("status", form.status);
    formData.append("deskripsi", form.deskripsi);
    form.files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });
    if (rawTags.value.trim()) {
      rawTags.value.split(',').map(t => t.trim()).filter(t => t.length).forEach((t, i) => formData.append(`tags[${i}]`, t))
    }
    try {
      const response = await axios.post(route("auditmutu.store"), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.success) {
        Swal.fire({ title: "Berhasil!", text: response.data.message, icon: "success", confirmButtonText: "OK" }).then(() => {
          const slugid = props.standars.data[0].slug;
          router.visit(route("auditmutu.adddocument", slugid), { preserveState: true, preserveScroll: true, replace: true });
          form.reset();
          form.files = [];
          if (fileInput.value) fileInput.value.value = "";
        });
      }
    } catch (error) {
      console.error("Error:", error.response?.data || error.message);
      Swal.fire({ title: "Gagal!", text: (error.response?.data?.message) || "Terjadi kesalahan", icon: "error", confirmButtonText: "OK" });
    }
  } else if (mode.value === 'folder') {
    // folder mode
    if (!folderName.value) {
      Swal.fire('Validasi', 'Nama Folder wajib diisi', 'warning');
      return;
    }
    const fd = new FormData();
    fd.append('stdid', form.stdid);
    if (form.parent_id) fd.append('parent_id', form.parent_id);
    fd.append('folder_name', folderName.value);
    if (folderFileNama.value) fd.append('file_nama', folderFileNama.value);
    if (folderFileDeskripsi.value) fd.append('file_deskripsi', folderFileDeskripsi.value);
    if (folderFile.value) fd.append('file', folderFile.value);
    if (rawTags.value.trim()) {
      rawTags.value.split(',').map(t => t.trim()).filter(t => t.length).forEach((t, i) => fd.append(`tags[${i}]`, t))
    }
    try {
      const response = await axios.post(route('auditmutu.folder.storeWithFile'), fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.success) {
        Swal.fire({ title: 'Berhasil!', text: response.data.message, icon: 'success', confirmButtonText: 'OK' }).then(() => {
          const slugid = props.standars.data[0].slug;
          router.visit(route('auditmutu.adddocument', slugid), { preserveState: true, preserveScroll: true, replace: true });
          // reset
          folderName.value = ''; folderFile.value = null; folderFileNama.value = ''; folderFileDeskripsi.value = '';
          if (fileInput.value) fileInput.value.value = '';
        });
      }
    } catch (error) {
      console.error('Error:', error.response?.data || error.message);
      Swal.fire({ title: 'Gagal!', text: (error.response?.data?.message) || 'Terjadi kesalahan', icon: 'error', confirmButtonText: 'OK' });
    }
  } else if (mode.value === 'folderTree') {
    if (!folderTreeFiles.value.length) {
      Swal.fire('Validasi', 'Pilih folder yang berisi file terlebih dahulu', 'warning');
      return;
    }
    const fd = new FormData();
    fd.append('stdid', form.stdid);
    if (form.parent_id) fd.append('parent_id', form.parent_id);
    // Append files and their relative paths
    folderTreeFiles.value.forEach((file, i) => {
      fd.append(`files[${i}]`, file);
      // webkitRelativePath tersedia di Chrome/Edge saat memilih folder
      const rel = file.webkitRelativePath || file.name;
      fd.append(`paths[${i}]`, rel);
    });
    if (rawTags.value.trim()) {
      rawTags.value.split(',').map(t => t.trim()).filter(t => t.length).forEach((t, i) => fd.append(`tags[${i}]`, t))
    }
    try {
      const response = await axios.post(route('auditmutu.folder.uploadTree'), fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.success) {
        Swal.fire({ title: 'Berhasil!', text: response.data.message, icon: 'success', confirmButtonText: 'OK' }).then(() => {
          const slugid = props.standars.data[0].slug;
          router.visit(route('auditmutu.adddocument', slugid), { preserveState: true, preserveScroll: true, replace: true });
          // reset state
          folderTreeFiles.value = [];
          if (fileInput.value) fileInput.value.value = '';
        });
      }
    } catch (error) {
      console.error('Error:', error.response?.data || error.message);
      Swal.fire({ title: 'Gagal!', text: (error.response?.data?.message) || 'Terjadi kesalahan', icon: 'error', confirmButtonText: 'OK' });
    }
  }
};
</script>
<template>
  <div class="items-center justify-center p-4 bg-opacity-50 rounded-lg shadow-md border-bg-gray-200">
    <form class="space-y-4" @submit.prevent="submit">
      <!-- Mode Toggle -->
      <div class="flex gap-4 text-sm">
        <label class="flex items-center gap-1 cursor-pointer">
          <input type="radio" value="file" v-model="mode" /> <span>Upload Dokumen</span>
        </label>
        <label class="flex items-center gap-1 cursor-pointer">
          <input type="radio" value="folder" v-model="mode" /> <span>Buat Folder (+ optional file)</span>
        </label>
        <label class="flex items-center gap-1 cursor-pointer">
          <input type="radio" value="folderTree" v-model="mode" /> <span>Unggah Folder (berisi subfolder)</span>
        </label>
      </div>
      <hr />

      <!-- FILE MODE -->
      <template v-if="mode === 'file'">
        <div>
          <label class="block text-sm font-medium text-gray-700">Nama Dokumen</label>
          <input type="text" v-model.trim="form.nama_dok"
            class="w-full px-2 py-1 mt-1 text-sm border rounded focus:ring-indigo-500 focus:border-indigo-500" />
          <InputError :message="form.errors.nama_dok" class="mt-2" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Tags (pisah koma)</label>
          <input type="text" v-model="rawTags" placeholder="misal: audit,laporan"
            class="w-full px-2 py-1 mt-1 text-sm border rounded focus:ring-indigo-500 focus:border-indigo-500" />
        </div>
        <div v-if="parentFolderId" class="flex items-center gap-2 text-xs">
          <span class="text-gray-500">Folder saat ini:</span>
          <span
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-100 text-indigo-800 border border-indigo-200">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-3 h-3">
              <path d="M2 6a2 2 0 012-2h3.5a1 1 0 01.8.4l1.2 1.6H16a2 2 0 012 2v5a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
            </svg>
            {{ parentFolderName || `#${parentFolderId}` }}
          </span>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Deskripsi</label>
          <textarea
            class="w-full p-2 mt-1 text-sm border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            rows="4" v-model="form.deskripsi" placeholder="Deskripsi"></textarea>
          <InputError :message="form.errors.deskripsi" class="mt-2" />
        </div>
        <div v-if="$page.props.auth.user.kodeprodi === null">
          <label class="block text-sm font-medium text-gray-700">Program Studi</label>
          <select v-model="form.prodi"
            class="block w-full px-3 py-2 mt-1 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500">
            <option value="">Pilih Prodi</option>
            <option v-for="prod in program.data" :key="prod.id" :value="prod.id">{{ prod.jenjang }} - {{ prod.name }}
            </option>
          </select>
          <InputError :message="form.errors.jenis_dok" class="mt-2" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Publikasi</label>
          <select v-model="form.status"
            class="block w-full px-3 py-2 mt-1 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500">
            <option value="">Pilih Publikasi</option>
            <option value="0">Eksternal</option>
            <option value="1">Internal</option>
          </select>
          <InputError :message="form.errors.status" class="mt-2" />
        </div>
        <div>
          <InputLabel value="File (boleh multi)" class="block text-sm font-medium text-gray-700" />
          <input type="file" ref="fileInput" multiple @change="handleFileChange"
            class="block w-full mt-1 text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer focus:ring-indigo-500 focus:border-indigo-500" />
          <progress v-if="form.progress" :value="form.progress.percentage" max="100" class="w-full mt-2">{{
            form.progress.percentage }}%</progress>
          <InputError :message="form.errors.file_path" class="mt-2" />
        </div>
      </template>

      <!-- FOLDER MODE -->
      <template v-else-if="mode === 'folder'">
        <div>
          <InputField label="Nama Folder" icon="folder" v-model.trim="folderName" />
        </div>
        <div class="flex items-center gap-2 text-xs" v-if="parentFolderId">
          <span class="text-gray-500">Folder induk:</span>
          <span
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800 border border-amber-200">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-3 h-3">
              <path d="M2 6a2 2 0 012-2h3.5a1 1 0 01.8.4l1.2 1.6H16a2 2 0 012 2v5a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
            </svg>
            {{ parentFolderName || `#${parentFolderId}` }}
          </span>
        </div>
        <div class="p-3 space-y-3 border rounded bg-gray-50">
          <p class="text-xs font-semibold">File Opsional di Dalam Folder</p>
          <InputField label="Nama File" icon="pen" v-model.trim="folderFileNama" />
          <div>
            <label class="block text-xs font-medium text-gray-600">Deskripsi File</label>
            <textarea rows="3" v-model="folderFileDeskripsi"
              class="w-full p-2 mt-1 text-xs border rounded focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Deskripsi file (opsional)"></textarea>
          </div>
          <div>
            <InputLabel value="File (maks 1)" class="block text-xs font-medium text-gray-700" />
            <input type="file" ref="fileInput" @change="handleFileChange"
              class="block w-full mt-1 text-xs text-gray-900 border border-gray-300 rounded cursor-pointer focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600">Tags (pisah koma)</label>
            <input type="text" v-model="rawTags" placeholder="misal: audit,laporan"
              class="w-full px-2 py-1 mt-1 text-xs border rounded focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
        </div>
      </template>

      <!-- FOLDER TREE UPLOAD MODE -->
      <template v-else>
        <div class="space-y-2">
          <p class="text-sm">Pilih sebuah folder dari komputer Anda. Semua subfolder dan file di dalamnya akan dibuat
            sesuai struktur.</p>
          <p class="text-xs text-gray-500">Catatan: Fitur ini didukung di Chrome/Edge. Browser akan mengirimkan jalur
            relatif (webkitRelativePath).</p>
          <div v-if="parentFolderId" class="flex items-center gap-2 text-xs">
            <span class="text-gray-500">Folder induk:</span>
            <span
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800 border border-amber-200">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-3 h-3">
                <path d="M2 6a2 2 0 012-2h3.5a1 1 0 01.8.4l1.2 1.6H16a2 2 0 012 2v5a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
              </svg>
              {{ parentFolderName || `#${parentFolderId}` }}
            </span>
          </div>
          <div>
            <InputLabel value="Pilih Folder" class="block text-sm font-medium text-gray-700" />
            <input type="file" @change="handleFolderTreeChange" webkitdirectory directory multiple
              class="block w-full mt-1 text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">Tags (pisah koma)</label>
            <input type="text" v-model="rawTags" placeholder="misal: audit,laporan"
              class="w-full px-2 py-1 mt-1 text-sm border rounded focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
          <div v-if="folderTreeFiles.length" class="text-xs text-gray-600">
            {{ folderTreeFiles.length }} file dipilih.
          </div>
        </div>
      </template>

      <div class="pt-2">
        <PrimaryBtn type="submit" class="w-full">Simpan</PrimaryBtn>
      </div>
    </form>
  </div>
</template>
